import { fx_crearBaseLocal, reqToPromise, txDone } from "../../utils/idbUtils";

const DB_NAME = "grpc_catalogos";
/** Subir al agregar/quitar un store de `GRPC_CATALOGO_STORES`, o al cambiar
 *  un índice: cada cambio de versión vacía esta base y la vuelve a llenar
 *  (`vaciarAntesDe: DB_VERSION`, ver `fx_crearBaseLocal`). Versión 7: une la 5
 *  de `ernesto` y la 6 de la rama del registro de finca (municipios y
 *  coberturas de lote no productivo, sin regiones); tiene que ser mayor que
 *  las dos para que quien tenga cualquiera de ellas la reconstruya.
 *
 *  **Versión 8 (2026-10-23, merge de `dev-carlos` en `ernesto`, decidida el
 *  2026-10-21):** suma el store `monitoreoCultivoPlagas` de `dev-carlos` (tabla de
 *  valores de referencia por cultivo, especificación v1.0, 3.2 B). En esa rama
 *  la 6 forzaba a todos los navegadores a volver a bajar los catálogos y la 7
 *  agregaba ese store; acá la 7 era la del registro de finca. Tiene que ser
 *  mayor que las dos 7 para que cualquier navegador la reconstruya. */
const DB_VERSION = 8;
const STORE_SYNC = "sync";
/** Índice de `"municipios"` por `departamentoId` (ver
 *  `getMunicipiosByDepartamentoIdAsync`) — ese store guarda items
 *  de los ~33 departamentos mezclados, hace falta para no traer todo y
 *  filtrar en JS. */
const INDEX_DEPARTAMENTO_ID = "departamentoId";

/** Nombre de cada store de catálogo -uno por catálogo migrado a gRPC-, más el
 *  store de sincronización. Agregar acá al sumar un catálogo nuevo. */
export const GRPC_CATALOGO_STORES = [
  "cultivos",
  "lotesNoProductivos",
  "cultivoTratamientos",
  "productos",
  "monitoreoPlagasEnfermedades",
  "monitoreoCultivoPlagas",
  "tiposAnimal",
  "razasAnimal",
  "procedimientosVeterinarios",
  "indicesSatelite",
  "departamentos",
  "municipios",
] as const;

export type GrpcCatalogoStoreName = (typeof GRPC_CATALOGO_STORES)[number];

/** Item mínimo que necesita el merge de delta: cualquier catálogo gRPC tiene
 *  que traer al menos esto (coincide con los primeros 2 campos de cada
 *  `*Item` generado por proto -"ultima_actualizacion" NO va: el filtro por
 *  watermark lo resuelve el server, el cliente nunca la leía de vuelta, ver
 *  CLAUDE.md del Server, 2026-09-15-). */
export type GrpcCatalogoItem = {
  id: string;
  eliminado: boolean;
};

/** IndexedDB dedicada para los catálogos migrados a gRPC-Web con
 *  sincronización delta (ver CLAUDE.md del Server, sección migración a
 *  gRPC-Web). A propósito, base propia y no un store compartido, mismo
 *  criterio que `GrpcFincaLoteDatabase`: cada isla gRPC abre su propia
 *  conexión IndexedDB (nombre y versión propios), así el upgrade de una no
 *  arriesga el resto.
 *
 *  A diferencia de `GrpcFincaLoteDatabase`, acá sí hay delta real (pedido
 *  explícito): `putDeltaAsync` aplica puts/deletes según `eliminado`, no un
 *  reemplazo completo, y esta clase guarda su propio watermark por catálogo
 *  (store `sync`).
 *
 *  Cada upgrade de `DB_VERSION` vacía y recrea todos los stores
 *  (`vaciarAntesDe: DB_VERSION` en `fx_crearBaseLocal`): antes solo se creaban
 *  los stores listados sin borrar los viejos, así que sacar un catálogo de
 *  `GRPC_CATALOGO_STORES` (ej. Municipio) dejaba su store huérfano para siempre
 *  en quien ya lo había sincronizado. Con esto, subir
 *  `DB_VERSION` alcanza para "restaurar" la base al esquema actual — se
 *  pierde el cache local de los catálogos, pero se reconstruye solo en el
 *  próximo login (misma resincronización completa que ya pasa la primera
 *  vez). */
class GrpcCatalogosDatabase {
  /** Conexión a la base local con el ayudante común. Esta base se vacía en
   *  cada cambio de `DB_VERSION` (ver `fx_crearBaseLocal`). */
  private readonly _base = fx_crearBaseLocal(
    DB_NAME,
    DB_VERSION,
    [
      ...GRPC_CATALOGO_STORES.map((nombre) => ({
        nombre,
        clave: "id",
        indices: nombre === "municipios" ? [INDEX_DEPARTAMENTO_ID] : [],
      })),
      { nombre: STORE_SYNC, clave: "storeName" },
    ],
    { vaciarAntesDe: DB_VERSION },
  );

  /** Conexión a la base local, abierta la primera vez y reusada después. */
  private open(): Promise<IDBDatabase> {
    return this._base.abrir();
  }

  /** `true` si el store ya tiene al menos un registro (para saber si mostrar
   *  algo mientras la primera sincronización está en vuelo). */
  async hasDataAsync(storeName: GrpcCatalogoStoreName): Promise<boolean> {
    const db = await this.open();
    const store = db.transaction(storeName, "readonly").objectStore(storeName);
    const count = await reqToPromise<number>(store.count());
    return count > 0;
  }

  /** Todo el contenido guardado de un catálogo. */
  async getListAsync<T>(storeName: GrpcCatalogoStoreName): Promise<T[]> {
    const db = await this.open();
    const store = db.transaction(storeName, "readonly").objectStore(storeName);
    return reqToPromise<T[]>(store.getAll());
  }

  /** Aplica un delta: `put` de los items vivos, `delete` de los marcados
   *  `eliminado`, y guarda el nuevo watermark. No toca nada si `items` viene
   *  vacío (nada cambió desde el watermark anterior), salvo que igual haya
   *  que avanzar el watermark si el server no tiene cambios: se guarda
   *  siempre, incluso con 0 items, para no repetir el
   *  `ultimaSincronizacion = 0` (listado completo) en cada sync. */
  async putDeltaAsync<T extends GrpcCatalogoItem>(
    storeName: GrpcCatalogoStoreName,
    items: readonly T[],
    nuevoWatermark: bigint,
  ): Promise<void> {
    const db = await this.open();
    const tx = db.transaction([storeName, STORE_SYNC], "readwrite");
    const store = tx.objectStore(storeName);
    const syncStore = tx.objectStore(STORE_SYNC);

    for (const item of items) {
      if (item.eliminado) {
        store.delete(item.id);
      } else {
        store.put(item);
      }
    }
    syncStore.put({ storeName, ultimaSincronizacion: nuevoWatermark.toString() });

    await txDone(tx);
  }

  /** Municipios ya guardados de un departamento puntual (vía el índice
   *  `departamentoId`) — vacío si nunca se pidieron. A diferencia de los
   *  otros stores, `"municipios"` no tiene watermark: la RPC que
   *  lo llena (`ListMunicipiosByDepartamentoId`) no acepta
   *  `ultima_sincronizacion` (es una consulta puntual, no un catálogo con
   *  delta, ver `catalogos_terceros.proto`), así que la presencia de datos
   *  para ese departamento es la única señal de "ya se pidió". */
  async getMunicipiosByDepartamentoIdAsync<T>(departamentoId: string): Promise<T[]> {
    const db = await this.open();
    const store = db.transaction("municipios", "readonly").objectStore("municipios");
    return reqToPromise<T[]>(store.index(INDEX_DEPARTAMENTO_ID).getAll(departamentoId));
  }

  /** Guarda los municipios recién pedidos de un departamento. Sin lógica de
   *  `eliminado` (a diferencia de `putDeltaAsync`): esta RPC siempre devuelve
   *  el listado vivo completo de ese departamento, nunca tombstones. */
  async putMunicipiosAsync<T extends { id: string }>(items: readonly T[]): Promise<void> {
    const db = await this.open();
    const tx = db.transaction("municipios", "readwrite");
    const store = tx.objectStore("municipios");
    for (const item of items) {
      store.put(item);
    }
    await txDone(tx);
  }

  /** Watermark guardado de la última sincronización exitosa de un catálogo.
   *  `0n` si todavía no sincronizó nunca (trae el listado completo). Se
   *  persiste como string -IndexedDB no serializa `bigint` en Safari/algunos
   *  motores- y se reconvierte acá. */
  async getUltimaSincronizacionAsync(storeName: GrpcCatalogoStoreName): Promise<bigint> {
    const db = await this.open();
    const store = db.transaction(STORE_SYNC, "readonly").objectStore(STORE_SYNC);
    const row = await reqToPromise<{ storeName: string; ultimaSincronizacion: string } | undefined>(
      store.get(storeName),
    );
    return row ? BigInt(row.ultimaSincronizacion) : 0n;
  }
}

/** Singleton de la base de catálogos gRPC (delta). */
export const grpcCatalogosDatabase = new GrpcCatalogosDatabase();
